import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { seasonAPI } from '../services/api';
import CreateSeasonModal from '../components/CreateSeasonModal';
import AddParticipantModal from '../components/AddParticipantModal';
import OptOutReasonModal from '../components/OptOutReasonModal';
import './ManageSeasons.css';

function ManageSeasons() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [seasons, setSeasons] = useState([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddParticipantModal, setShowAddParticipantModal] = useState(false);
  const [showOptOutModal, setShowOptOutModal] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);

  // Load seasons on mount
  useEffect(() => {
    loadSeasons();
  }, []);

  // Load season data when selection changes
  useEffect(() => {
    if (selectedSeasonId) {
      loadSeasonData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSeasonId]);

  const loadSeasons = async () => {
    try {
      setLoading(true);
      const response = await seasonAPI.getAllSeasons();
      setSeasons(response.data.seasons || []);

      // Auto-select the active season or the most recent season
      if (response.data.seasons.length > 0) {
        const activeSeason = response.data.seasons.find((s) => s.is_active);
        const defaultSeason = activeSeason || response.data.seasons[0];
        setSelectedSeasonId(defaultSeason.id);
        setSelectedSeason(defaultSeason);
      }
    } catch (err) {
      console.error('Failed to load seasons:', err);
      setError('Failed to load seasons');
    } finally {
      setLoading(false);
    }
  };

  const loadSeasonData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load participants
      const participantsResponse = await seasonAPI.getParticipants(selectedSeasonId);
      setParticipants(participantsResponse.data.participants || []);

      // Load teams
      const teamsResponse = await seasonAPI.getSeasonTeams(selectedSeasonId);
      setTeams(teamsResponse.data.teams || []);

      // Set selected team IDs
      const selectedIds = teamsResponse.data.teams.filter((t) => t.in_season).map((t) => t.id);
      setSelectedTeamIds(selectedIds);
    } catch (err) {
      console.error('Failed to load season data:', err);
      setError('Failed to load season data');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleSeasonChange = (e) => {
    const seasonId = parseInt(e.target.value);
    setSelectedSeasonId(seasonId);
    const season = seasons.find((s) => s.id === seasonId);
    setSelectedSeason(season);
  };

  const handleCreateSeason = async (seasonData) => {
    try {
      await seasonAPI.createSeason(seasonData);
      showToast('Season created successfully!');
      await loadSeasons();
      setShowCreateModal(false);
    } catch (error) {
      throw error; // Let modal handle the error
    }
  };

  const handleAddParticipant = async (userIds) => {
    try {
      // Handle both single user (for backward compatibility) and array of users
      if (Array.isArray(userIds)) {
        const response = await seasonAPI.addParticipantsBulk(selectedSeasonId, userIds);
        const count = response.data.added_count || userIds.length;
        showToast(`${count} participant${count !== 1 ? 's' : ''} added successfully!`);
      } else {
        await seasonAPI.addParticipant(selectedSeasonId, userIds);
        showToast('Participant added successfully!');
      }
      await loadSeasonData();
      setShowAddParticipantModal(false);
    } catch (error) {
      throw error; // Let modal handle the error
    }
  };

  const handleRemoveParticipant = async (participant) => {
    if (
      !window.confirm(
        `Remove ${participant.username} from this season? This action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      await seasonAPI.removeParticipant(selectedSeasonId, participant.user_id);
      showToast(`${participant.username} removed from season`);
      await loadSeasonData();
    } catch (err) {
      console.error('Failed to remove participant:', err);
      showToast(err.response?.data?.error || 'Failed to remove participant', 'error');
    }
  };

  const handleOptOut = (participant) => {
    setSelectedParticipant(participant);
    setShowOptOutModal(true);
  };

  const handleOptOutSubmit = async (reason) => {
    try {
      await seasonAPI.optOutParticipant(selectedSeasonId, selectedParticipant.user_id, reason);
      showToast(`${selectedParticipant.username} opted out`);
      await loadSeasonData();
      setShowOptOutModal(false);
      setSelectedParticipant(null);
    } catch (error) {
      throw error; // Let modal handle the error
    }
  };

  const handleOptIn = async (participant) => {
    if (!window.confirm(`Re-activate ${participant.username} in this season?`)) {
      return;
    }

    try {
      await seasonAPI.optInParticipant(selectedSeasonId, participant.user_id);
      showToast(`${participant.username} re-activated`);
      await loadSeasonData();
    } catch (err) {
      console.error('Failed to re-activate participant:', err);
      showToast(err.response?.data?.error || 'Failed to re-activate participant', 'error');
    }
  };

  const handleTeamToggle = (teamId) => {
    setSelectedTeamIds((prev) => {
      if (prev.includes(teamId)) {
        return prev.filter((id) => id !== teamId);
      } else {
        return [...prev, teamId];
      }
    });
  };

  const handleSaveTeams = async () => {
    try {
      await seasonAPI.updateSeasonTeams(selectedSeasonId, selectedTeamIds);
      showToast(`Season teams updated: ${selectedTeamIds.length} teams selected`);
      await loadSeasonData();
    } catch (err) {
      console.error('Failed to update teams:', err);
      showToast(err.response?.data?.error || 'Failed to update teams', 'error');
    }
  };

  const handleSetActive = async (seasonId) => {
    const season = seasons.find((s) => s.id === seasonId);
    if (!season) return;

    if (
      !window.confirm(
        `Set ${season.year} as the active season? This will deactivate all other seasons.`
      )
    ) {
      return;
    }

    try {
      await seasonAPI.setActiveSeason(seasonId);
      showToast(`Season ${season.year} is now active`);
      await loadSeasons();
    } catch (err) {
      console.error('Failed to set active season:', err);
      showToast(err.response?.data?.error || 'Failed to set active season', 'error');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCombinedStats = (completed, total, rate) => {
    const completedVal = completed || 0;
    const totalVal = total || 0;
    const rateVal = rate || 0;
    return `${completedVal}/${totalVal} (${parseFloat(rateVal).toFixed(0)}%)`;
  };

  if (loading && seasons.length === 0) {
    return <div className="loading">Loading seasons...</div>;
  }

  if (error && seasons.length === 0) {
    return (
      <div className="error-container">
        <p className="error-message">{error}</p>
        <button onClick={loadSeasons} className="btn btn-primary">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="manage-seasons-container">
      <div className="page-header">
        <button onClick={() => navigate('/picks')} className="btn-back">
          ← Back to Picks
        </button>
        <h1>Manage Seasons</h1>
        <button onClick={() => setShowCreateModal(true)} className="btn btn-primary">
          + Create New Season
        </button>
      </div>

      {seasons.length === 0 ? (
        <div className="no-seasons">
          <p>No seasons found. Create your first season to get started.</p>
        </div>
      ) : (
        <>
          <div className="season-selector-container">
            <div className="season-selector">
              <label htmlFor="season-select">Selected Season:</label>
              <select
                id="season-select"
                value={selectedSeasonId || ''}
                onChange={handleSeasonChange}
                className="season-select"
              >
                {seasons.map((season) => (
                  <option key={season.id} value={season.id}>
                    {season.year} {season.is_active ? '(Active)' : ''}
                  </option>
                ))}
              </select>
            </div>
            {selectedSeason && (
              <div className="season-info">
                <span className="season-dates">
                  {formatDate(selectedSeason.start_date)} - {formatDate(selectedSeason.end_date)}
                </span>
                {selectedSeason.is_active ? (
                  <span className="season-active-badge">Active</span>
                ) : (
                  user?.role === 'super_admin' && (
                    <button
                      onClick={() => handleSetActive(selectedSeason.id)}
                      className="btn btn-sm btn-success"
                      style={{ fontSize: '0.85rem', padding: '0.4rem 0.8rem' }}
                    >
                      Set as Active
                    </button>
                  )
                )}
              </div>
            )}
          </div>

          {/* Season Participants Section */}
          <section className="season-section">
            <div className="section-header">
              <h2>Season Participants</h2>
              <button
                onClick={() => setShowAddParticipantModal(true)}
                className="btn btn-secondary"
              >
                + Add Participant
              </button>
            </div>

            <div className="participants-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th className="hide-mobile">Username</th>
                    <th>Display Name</th>
                    <th className="hide-mobile">Enrolled</th>
                    <th className="hide-mobile">Last Active</th>
                    <th className="hide-mobile">Rounds</th>
                    <th className="hide-mobile">Picks</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {participants.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="no-results">
                        No participants in this season. Add participants to get started.
                      </td>
                    </tr>
                  ) : (
                    participants.map((participant) => (
                      <tr key={participant.id}>
                        <td className="username-cell hide-mobile">{participant.username}</td>
                        <td>{participant.display_name || '-'}</td>
                        <td className="hide-mobile">{formatDate(participant.enrolled_date)}</td>
                        <td className="hide-mobile">{formatDate(participant.last_active_date)}</td>
                        <td className="hide-mobile">
                          {formatCombinedStats(
                            participant.rounds_participated,
                            participant.rounds_available,
                            participant.participation_rate
                          )}
                        </td>
                        <td className="hide-mobile">
                          {formatCombinedStats(
                            participant.picks_made,
                            participant.picks_available,
                            participant.pick_completion_rate
                          )}
                        </td>
                        <td>
                          {participant.is_active ? (
                            <span className="status-badge active">Active</span>
                          ) : (
                            <span
                              className="status-badge inactive"
                              title={participant.opted_out_reason || ''}
                            >
                              Opted Out
                            </span>
                          )}
                        </td>
                        <td className="actions-cell">
                          {participant.is_active ? (
                            <>
                              <button
                                onClick={() => handleOptOut(participant)}
                                className="btn-action btn-opt-out"
                              >
                                Opt Out
                              </button>
                              <button
                                onClick={() => handleRemoveParticipant(participant)}
                                className="btn-action btn-remove"
                              >
                                Remove
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => handleOptIn(participant)}
                              className="btn-action btn-opt-in"
                            >
                              Re-activate
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="section-summary">
              Total Participants: {participants.length}
              (Active: {participants.filter((p) => p.is_active).length})
            </div>
          </section>

          {/* Season Teams Section */}
          <section className="season-section">
            <div className="section-header">
              <h2>Season Teams</h2>
              <div className="team-count">
                {selectedTeamIds.length} of {teams.length} teams selected
              </div>
            </div>

            <div className="teams-grid">
              {teams.map((team) => (
                <label key={team.id} className="team-checkbox-item">
                  <input
                    type="checkbox"
                    checked={selectedTeamIds.includes(team.id)}
                    onChange={() => handleTeamToggle(team.id)}
                  />
                  <span className="team-name">{team.name}</span>
                  {team.city && <span className="team-city">({team.city})</span>}
                </label>
              ))}
            </div>

            <div className="section-actions">
              <button onClick={handleSaveTeams} className="btn btn-primary">
                Save Teams
              </button>
            </div>
          </section>
        </>
      )}

      {showCreateModal && (
        <CreateSeasonModal onClose={() => setShowCreateModal(false)} onSave={handleCreateSeason} />
      )}

      {showAddParticipantModal && (
        <AddParticipantModal
          seasonId={selectedSeasonId}
          existingParticipants={participants}
          onClose={() => setShowAddParticipantModal(false)}
          onSave={handleAddParticipant}
        />
      )}

      {showOptOutModal && (
        <OptOutReasonModal
          participant={selectedParticipant}
          onClose={() => {
            setShowOptOutModal(false);
            setSelectedParticipant(null);
          }}
          onSave={handleOptOutSubmit}
        />
      )}

      {toast && <div className={`toast toast-${toast.type}`}>{toast.message}</div>}
    </div>
  );
}

export default ManageSeasons;
